import {
    arrow,
    autoUpdate,
    FloatingArrow,
    FloatingFocusManager,
    offset,
    safePolygon,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole
} from '@floating-ui/react';
import {
    faBook,
    faChalkboardTeacher,
    faEnvelope,
    faFileInvoice,
    faKey,
    faMapPin,
    faStoreAlt,
    faTicketAlt,
    faUsers,
    faWindowMaximize
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import Link from 'next/link';
import React, { useCallback, useState } from 'react';
import { imageSource } from '../../helpers/ImageHelper';
import useAuth from '../../hooks/useAuth';
import useMediaQuery from '../../hooks/useMediaQuery';

const ClubQuickLinks = () => {
    const { user } = useAuth();
    const clubs = user?.clubs || [];

    if (clubs.length === 0) return null;

    return (
        <div className="tw-fixed tw-left-0 tw-top-1/2 tw-z-[999] tw-flex -tw-translate-y-1/2 tw-flex-col tw-items-center tw-justify-around tw-rounded-r-xl tw-bg-white tw-shadow-lg">
            {clubs.map((club) => (
                <QuickLinks key={club.slug} club={club} />
            ))}
        </div>
    );
};

const QuickLinks = ({ club }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width: 991px)');
    const arrowRef = React.useRef<SVGSVGElement>(null);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [
            offset(14),
            arrow({
                element: arrowRef
            })
        ],
        whileElementsMounted: autoUpdate,
        placement: 'right'
    });

    const hover = useHover(context, {
        handleClose: safePolygon()
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

    const getPagesForClub = useCallback(
        (clubSlug: string) => [
            {
                title: 'Dashboard',
                link: `/club/${clubSlug}/general`,
                icon: faStoreAlt
            },
            {
                title: 'Nachrichten Management',
                link: `/club/${clubSlug}/messages`,
                icon: faEnvelope
            },
            {
                title: 'Mitglieder Management',
                link: `/club/${clubSlug}/members`,
                icon: faUsers
            },
            {
                title: 'Platzbuchungssystem',
                link: `/club/${clubSlug}/spaces`,
                icon: faMapPin
            },
            {
                title: 'Trainer Buchungen',
                link: `/club/${clubSlug}/trainer-booking`,
                icon: faChalkboardTeacher
            },
            {
                title: 'Event Buchungen',
                link: `/club/${clubSlug}/bookings`,
                icon: faTicketAlt
            },
            {
                title: 'Trainingsjournal',
                link: `/club/${clubSlug}/journal`,
                icon: faBook
            },
            {
                title: 'Rechnungen',
                link: `/club/${clubSlug}/invoices`,
                icon: faFileInvoice
            },
            {
                title: 'Zutrittskontrolle',
                link: `/club/${clubSlug}/access`,
                icon: faKey
            },
            {
                title: 'Gutscheine',
                link: `/club/${clubSlug}/vouchers`,
                icon: faWindowMaximize
            }
        ],
        []
    );

    if (isMobile) return null;

    return (
        <>
            <div ref={refs.setReference} {...getReferenceProps()}>
                <Link
                    href={`/club/${club.slug}`}
                    className="tw-relative tw-m-2 tw-flex tw-h-[36px] tw-w-[36px] tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-border tw-border-solid tw-border-accents-1 tw-text-accents-8"
                >
                    {club.logo ? (
                        <Image src={imageSource(club.logo)} objectFit="cover" layout="fill" alt={club.title} />
                    ) : (
                        <div className="tw-text-lg">{club.title.slice(0, 2).toUpperCase()}</div>
                    )}
                </Link>
            </div>
            {isOpen && (
                <FloatingFocusManager context={context} modal={false}>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="tw-z-[999] tw-flex tw-flex-col tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-lg"
                    >
                        <FloatingArrow className="tw-fill-white" ref={arrowRef} context={context} />
                        <span className="tw-p-2 tw-text-lg tw-font-semibold tw-text-accents-8">{club.title}</span>
                        <div className="tw-flex tw-items-center tw-gap-2">
                            {getPagesForClub(club.slug).map((page) => (
                                <div key={page.link}>
                                    <Link
                                        href={page.link}
                                        className="tw-group tw-flex tw-min-w-[40px] tw-items-center tw-justify-center tw-rounded-lg tw-px-2 tw-py-3 tw-text-accents-9 tw-transition-colors hover:tw-bg-successLight hover:tw-text-success focus:tw-outline-success"
                                    >
                                        <FontAwesomeIcon icon={page.icon} />
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </FloatingFocusManager>
            )}
        </>
    );
};

export default ClubQuickLinks;
